import { AccountSettings, Alert, Authenticator, Avatar, Button, Card, Flex, Heading, SelectField, TextField, useAuthenticator, useTheme, View } from "@aws-amplify/ui-react";
import { CustomConfirmSignUp } from "./CustomConfirmSignUp";
import { signUp as amplifySignUp } from 'aws-amplify/auth';
import { FileUploader, StorageImage } from '@aws-amplify/ui-react-storage';
import { deleteUser, fetchUserAttributes, FetchUserAttributesOutput, signOut } from "aws-amplify/auth";
import { Hub } from 'aws-amplify/utils';
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from 'react-router-dom';
import { GrUpload } from "react-icons/gr";
import I18nTextUtils from "../utils/i18n/I18nTextUtils";
import PublicUserProfileUtils from "../utils/PublicUserProfileUtils";
import StorageUtils from "../utils/StorageUtils";

import './UserInfoPopup.css';

import ImageUtils, { HandleProfileImageUploadParams } from "../utils/image/ImageUtils";
import "./UserInfoPopupProfileEditor.css";
import ReportProblemOverlay from "./ReportProblemOverlay";
import LegalNoticePopup from "./LegalNoticePopup";


export default function UserInfoPopup({ handleCloseOverlay }: { handleCloseOverlay: () => void }) {

    const navigate = useNavigate();
    const location = useLocation();

    const { t } = useTranslation();

    const { user } = useAuthenticator(context => [context.user]);

    const { tokens } = useTheme();

    const [userAttributes, setUserAttributes] = useState<FetchUserAttributesOutput>()

    const [showFileUpload, setShowFileUpload] = useState<boolean>(false)
    const [uploaderInError, setUploaderInError] = useState<boolean>(false)
    const [showReportOverlay, setShowReportOverlay] = useState<boolean>(false)
    const [showLegalNotice, setShowLegalNotice] = useState<boolean>(false)

    // Track if URL update is from internal navigation (to avoid loops)
    const isInternalNavigationRef = useRef<boolean>(false);

    const fileUploadRef = useRef(null)

    function handleSignOut(): void {
        signOut();
        handleCloseOverlay();
    }

    const handleShowReportOverlay = () => {
        isInternalNavigationRef.current = true;
        navigate('/user/report');
        setShowReportOverlay(true);
    };

    const handleCloseReportOverlay = () => {
        isInternalNavigationRef.current = true;
        navigate('/user');
        setShowReportOverlay(false);
    };

    const handleShowLegalNotice = () => {
        isInternalNavigationRef.current = true;
        navigate('/user/legal');
        setShowLegalNotice(true);
    };

    const handleCloseLegalNotice = () => {
        isInternalNavigationRef.current = true;
        navigate('/user');
        setShowLegalNotice(false);
    };

    // Handle URL changes - sync URL with overlay state
    useEffect(() => {
        // Skip if this is our own navigation (to avoid loops)
        if (isInternalNavigationRef.current) {
            isInternalNavigationRef.current = false;
            return;
        }

        const pathname = location.pathname;

        if (pathname === '/user/legal') {
            setShowLegalNotice(true);
            setShowReportOverlay(false);
        } else if (pathname === '/user/report') {
            setShowReportOverlay(true);
            setShowLegalNotice(false);
        } else if (pathname === '/user') {
            setShowLegalNotice(false);
            setShowReportOverlay(false);
        }
    }, [location.pathname]);

    useEffect(() => {

        if (user) {
            const setUser = async () => {
                try {
                    const currentUserAttributes: FetchUserAttributesOutput = await fetchUserAttributes()

                    setUserAttributes(currentUserAttributes)
                } catch (error) {
                    setUserAttributes(undefined)
                }
            }

            setUser()
        } else {
            setUserAttributes(undefined)

        }

        const hubListenerCancelToken = Hub.listen('auth', ({ payload }) => {

            switch (payload.event) {
                case 'signedIn':
                    handleCloseOverlay();
                    break;
            }
        });

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                handleCloseOverlay();
            }
        };
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);

            // Unsubscibe listener
            hubListenerCancelToken()
        }
    }, [showFileUpload]);

    const handleUploaderError = () => {
        setUploaderInError(true)

        // @ts-ignore - ignore .clearFiles() does not exists, looks like ref.current is not typed
        fileUploadRef.current?.clearFiles()
    }

    const resetUploaderError = () => {
        setUploaderInError(false)
    }

    const handleAvatarClicked = () => {
        setShowFileUpload(true)
    }

    const hideFileUpload = () => {
        setShowFileUpload(false)
        setUploaderInError(false)
    }

    const handleDeleteUser = async () => {
        console.warn("Handle delete user.");
        PublicUserProfileUtils.handleUserDeletion(user.userId)
        // TODO add profile image deletion/move to deleted data
        console.warn("Handle delete user - after profile deletion.");
        await deleteUser();
    };

    /**
     * This function is only for logging image size. Currently not used.
     *  
     * @param file 
     * @param imageName 
     * @returns 
     */
    function getImageResolution(file: File, imageName: String): Promise<{ width: number; height: number }> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = () => {
                const img = new Image();

                img.onload = () => {
                    const width = img.width;
                    const height = img.height;
                    console.warn(`Image ${imageName} resolution: ${width} x ${height}`);
                    resolve({ width, height });
                };

                img.onerror = (error) => {
                    console.error('Error loading image:', error);
                    reject(new Error('Failed to load image'));
                };

                img.src = reader.result as string;
            };

            reader.onerror = (error) => {
                console.error('Error reading file:', error);
                reject(new Error('Failed to read file'));
            };

            reader.readAsDataURL(file);
        });
    }

    const handleProcessFile = async (fileParams: { file: File; key: string; }) => {
        const params: HandleProfileImageUploadParams = {
            file: fileParams.file,
            key: fileParams.key,
            hideFileUpload: hideFileUpload,
            resetUploaderError: resetUploaderError,
            user: user
        }

        return ImageUtils.handleProfileImageUpload(params)
    }

    return (
        <View className="overlay-content"
            // TODO move style to .css
            backgroundColor={tokens.colors.background.secondary}
        >
            {/* Fixed Top Bar */}
            <View className="place-overview-top-bar">
                <Flex 
                    className="place-overview-top-bar-content"
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={tokens.space.medium}
                >
                    <Flex className="place-overview-top-bar-left" flex="1">
                    </Flex>
                    <Flex
                        className="place-overview-top-bar-right"
                        direction="row"
                        alignItems="center"
                        gap={tokens.space.small}
                    >
                        <Button
                            className="place-overview-close-button"
                            variation="link"
                            size="large"
                            onClick={handleCloseOverlay}
                        >
                            ✕
                        </Button>
                    </Flex>
                </Flex>
            </View>

            <View className="overlay-content-scrollable"
                padding={tokens.space.medium}
            >
                {showLegalNotice ? (
                    <LegalNoticePopup handleCloseOverlay={handleCloseLegalNotice} />
                ) : showReportOverlay ? (
                    <ReportProblemOverlay handleCloseOverlay={handleCloseReportOverlay} />
                ) : (
                    <Card>
                    {user &&
                        <Flex
                            direction="column"
                        >
                            <Heading
                                level={2}
                                fontWeight={tokens.fontWeights.bold}
                            >
                                {userAttributes ? userAttributes.nickname : "#"}
                            </Heading>
                            <TextField
                                label=""
                                labelHidden
                                value={user.signInDetails?.loginId}
                                isDisabled
                            />
                            {/* TODO - use devices cameras + test with Android! */}
                            <Avatar
                                className="profile-image-avatar"
                                onClick={handleAvatarClicked}
                            >
                                <StorageImage
                                    className="profile-image"
                                    alt=""
                                    bucket={StorageUtils.PHOTO_STORAGE_BUCKET_NAME}
                                    path={({ identityId }) => StorageUtils.PROFILE_PICTURES + `/${identityId}/${user.userId}.jpg`}
                                    onGetUrlError={(error) => console.error("OnGetUrlError: ", error)}
                                />
                                <GrUpload
                                    className="profile-image-upload-icon"
                                    // Size have not be here, does not work from css
                                    size="5rem"
                                />
                            </Avatar>
                            {showFileUpload &&
                                <div className="profile-image-upload">
                                    <div className={"profile-image-uploader" + (uploaderInError ? " in-error" : "")}>
                                        {!uploaderInError &&
                                            <Flex
                                                direction="column"
                                            >
                                                <FileUploader
                                                    acceptedFileTypes={['image/*']}
                                                    bucket={StorageUtils.PHOTO_STORAGE_BUCKET_NAME}
                                                    path={({ identityId }) => StorageUtils.PROFILE_PICTURES + `/${identityId}/`}
                                                    maxFileCount={1}
                                                    isResumable={false}
                                                    autoUpload={true}
                                                    displayText={{
                                                        dropFilesText: `${t("file-upload.drop-files-here-or")}`,
                                                        browseFilesText: `${t("file-upload.browse-files")}`,
                                                    }}
                                                    // TODO how to stop processing from process file?
                                                    processFile={handleProcessFile}
                                                    onUploadSuccess={() => {
                                                        hideFileUpload()
                                                    }}
                                                    onUploadStart={() => {
                                                        resetUploaderError()
                                                    }}
                                                    onUploadError={(error) => {
                                                        handleUploaderError()

                                                        return error;
                                                    }}
                                                    onFileRemove={() => {
                                                        resetUploaderError()
                                                    }}
                                                    ref={fileUploadRef}
                                                >
                                                </FileUploader>
                                                <Button
                                                    variation="warning"
                                                    onClick={hideFileUpload}
                                                >
                                                    Cancel upload
                                                </Button>
                                            </Flex>
                                        }
                                    </div>
                                    {uploaderInError &&
                                        <Alert
                                            heading={`${t("file-upload.file-upload-failed")}`}
                                            variation="error"
                                            hasIcon
                                            isDismissible
                                            onDismiss={hideFileUpload}
                                        >
                                            <Button
                                                onClick={resetUploaderError}
                                            >
                                                {`${t("file-upload.try-upload-again")}`}
                                            </Button>
                                        </Alert >
                                    }
                                </div>
                            }
                        </Flex>
                    }
                    <br />
                    <SelectField
                        label={t('localization.select-language')}
                        value={I18nTextUtils.getCurrentLanguage()}
                        onChange={(e) => I18nTextUtils.changeLanguage(e.target.value, undefined)}
                    >
                        {I18nTextUtils.getLanguageDetailList().map((ld) =>
                            <option key={ld.languageCode} value={ld.languageCode}>{ld.languageName}</option>
                        )}

                    </SelectField>
                    <br />
                    <Flex
                        direction="column"
                    >
                        <Authenticator 
                            components={{
                                ConfirmSignUp: CustomConfirmSignUp as any,
                            } as any}
                            services={{
                                async handleSignUp(input: any) {
                                    // Get current language preference
                                    const currentLanguage = I18nTextUtils.getCurrentLanguage();
                                    console.log('[UserInfoPopup] Sign-up with language:', currentLanguage);
                                    console.log('[UserInfoPopup] Sign-up input:', input);
                                    
                                    // Prepare options with clientMetadata
                                    const options: any = {
                                        ...(input.options || {}),
                                        clientMetadata: {
                                            language: currentLanguage,
                                        },
                                    };
                                    
                                    // Call signUp with clientMetadata to pass language
                                    return await amplifySignUp({
                                        username: input.username,
                                        password: input.password,
                                        options,
                                    });
                                },
                                // Note: handleResendSignUpCode removed - using direct call in CustomConfirmSignUp
                                // to avoid double emails. The custom component handles resend directly.
                            } as any}
                        />
                        {user &&
                            <Button
                                disabled
                            >
                                {t('user.update-nickname')}
                            </Button>
                        }
                        {user &&
                            <Button
                                onClick={handleSignOut}
                            >
                                {t('user.sign-out')}
                            </Button>
                        }
                        
                        {/* Privacy and Report links */}
                        <Flex 
                            direction="row" 
                            justifyContent="space-between" 
                            alignItems="center"
                            marginTop={tokens.space.small}
                            marginBottom={tokens.space.small}
                        >
                            <Button
                                variation="link"
                                size="small"
                                className="privacy-legal-link"
                                onClick={handleShowLegalNotice}
                            >
                                {t('user.privacy-legal')}
                            </Button>
                            <Button
                                variation="link"
                                size="small"
                                className="report-problem-link"
                                onClick={handleShowReportOverlay}
                            >
                                {t('user.report-problem')}
                            </Button>
                        </Flex>
                        
                        {user &&
                            <AccountSettings.DeleteUser
                                displayText={{
                                    cancelButtonText: t('user.delete-account-cancel'),
                                    confirmDeleteButtonText: t('user.delete-account-confirm'),
                                    deleteAccountButtonText: t('user.delete-account'),
                                    warningText: t('user.delete-account-warning'),
                                }}
                                handleDelete={handleDeleteUser}
                            />
                        }

                    </Flex>
                </Card>
                )}
            </View>
        </View>
    )
}
